/**
 * Date utility functions for handling timezone-aware datetime operations
 * Specifically for datetime-local input compatibility
 */

/**
 * Converts a Date object to local datetime string for datetime-local input
 * @param {Date} date - The date to convert (defaults to current date)
 * @returns {string} - YYYY-MM-DDTHH:MM format in local timezone
 */
export const toLocalDateTimeString = (date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60000; // offset in milliseconds
  const localTime = new Date(date.getTime() - offset);
  return localTime.toISOString().slice(0, 16);
}

/**
 * Converts a datetime-local input value to proper UTC ISO string
 * @param {string} datetimeLocalValue - YYYY-MM-DDTHH:MM format from datetime-local input
 * @returns {string} - ISO string in UTC
 */
export const fromLocalDateTimeString = (datetimeLocalValue) => {
  if (!datetimeLocalValue) {
    return new Date().toISOString();
  }
  
  // datetime-local gives us YYYY-MM-DDTHH:MM in local time
  // We need to create a Date object that represents this local time
  const localDate = new Date(datetimeLocalValue + ':00'); // Add seconds
  return localDate.toISOString();
}

/**
 * Converts an ISO string to local datetime string for editing
 * @param {string} isoString - ISO string from database
 * @returns {string} - YYYY-MM-DDTHH:MM format in local timezone
 */
export const isoToLocalDateTimeString = (isoString) => {
  if (!isoString) {
    return toLocalDateTimeString();
  }
  
  const date = new Date(isoString);
  return toLocalDateTimeString(date);
}

/**
 * Formats a date for display in local timezone
 * @param {string|Date} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string
 */
export const formatLocalDate = (date, options = {}) => {
  if (!date) return '—';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  };
  
  return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
}

/**
 * Gets the user's timezone name
 * @returns {string} - Timezone name (e.g., "America/Los_Angeles")
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Gets the current timezone offset in minutes
 * @returns {number} - Offset in minutes (negative for timezones west of UTC)
 */
export const getTimezoneOffset = () => {
  return new Date().getTimezoneOffset();
}